import { permissions } from "src/grading/utils/permissionsHelpers";
import { Permission } from "src/models";

export type NavSection =
  | "today"
  | "sessions"
  | "classes"
  | "people"
  | "school"
  | "reports"
  | "district";

export interface NavItem {
  key: NavSection;
  path: string;
  /** i18n key. */
  label: string;
  /** Shown when the user has any of these permissions. Omit to always show. */
  permission?: Permission[];
  /** Hidden when the user has any of these permissions (a broader item covers the section). */
  hiddenWith?: Permission[];
}

/** Placeholder in an item's path for the viewer's own school (`scopes.schoolIds[0]`). */
export const OWN_SCHOOL = ":ownSchoolId";

export const navItems: NavItem[] = [
  { key: "today", path: "/grading/today", label: "nav.today" },
  { key: "sessions", path: "/grading/sessions", label: "nav.sessions" },
  { key: "classes", path: "/grading/classes", label: "nav.classes" },
  {
    key: "people",
    path: "/grading/people",
    label: "nav.people",
    permission: [
      permissions.READ_STAFF,
      permissions.READ_SCHOOL_STUDENTS,
      permissions.READ_CLASS_STUDENTS,
    ],
  },
  {
    // School admins reach their school here; district viewers reach it through District (FR-005).
    key: "school",
    path: `/grading/schools/${OWN_SCHOOL}`,
    label: "nav.school",
    permission: [permissions.UPDATE_OWN_SCHOOL],
    hiddenWith: [permissions.READ_OWN_DISTRICT, permissions.READ_DISTRICTS],
  },
  { key: "reports", path: "/grading/reports", label: "nav.reports" },
  {
    key: "district",
    path: "/grading/districts",
    label: "nav.district",
    permission: [permissions.READ_OWN_DISTRICT, permissions.READ_DISTRICTS],
  },
];

const startsWithSegment = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(`${base}/`);

/**
 * The nav section a path belongs to. School pages belong to District, or to School for viewers
 * who see the School item instead (`schoolNav`).
 */
export const sectionFor = (
  pathname: string,
  { schoolNav = false }: { schoolNav?: boolean } = {},
): NavSection | null => {
  if (startsWithSegment(pathname, "/grading/today")) return "today";
  if (startsWithSegment(pathname, "/grading/sessions")) return "sessions";
  if (
    startsWithSegment(pathname, "/grading/classes") ||
    startsWithSegment(pathname, "/grading/students")
  ) {
    return "classes";
  }
  if (startsWithSegment(pathname, "/grading/people")) return "people";
  if (schoolNav && startsWithSegment(pathname, "/grading/schools")) {
    return "school";
  }
  if (startsWithSegment(pathname, "/grading/reports")) return "reports";
  if (
    startsWithSegment(pathname, "/grading/districts") ||
    startsWithSegment(pathname, "/grading/schools")
  ) {
    return "district";
  }
  return null;
};
